import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, isNonNullish } from 'remeda'
import { useExchangeRates } from '@components/ExchangeRatesProvider'
import { QuickAddDropdown } from '@components/QuickAddDropdown/QuickAddDropdown'
import { useCart } from '@velasca/hooks/use-cart'
import type { Image, Product, ProductSize } from '@velasca/types'
import { ConditionalLink } from '@velasca/ui/conditional-link'
import { HorizontalCardPrice } from '@velasca/ui/horizontal-card-price'
import { HorizontalCardTitle } from '@velasca/ui/horizontal-card-title'
import { SansText } from '@velasca/ui/sans-text'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { getFeaturedImage } from '@velasca/utils/get-featured-image'
import { isStoreExclusiveProduct } from '@velasca/utils/is-store-exclusive-product'
import { setQueryParams } from '@velasca/utils/set-query-params'
import {
  Container,
  Details,
  DetailsFooter,
  EmptyImage,
  Header,
  ImageWrapper,
  LoadingImage,
  Price,
  ProductDetails,
  ProductImage,
  ProductTitleLink,
  RemoveButton,
} from './WishlistLineItem.styles'

export interface WishlistLineItemProps {
  id?: Product['id']
  title: Product['title']
  handle?: Product['handle']
  images: Image[]
  isGiftCard?: Product['isGiftCard']
  customProductType?: string
  frontImageIndex?: string
  productType?: Product['productType']
  seoCaption?: string
  rawPrice?: Product['rawPrice']
  rawCompareAtPrice?: number
  material?: string
  color?: string
  sizes?: ProductSize[]
  storeExclusive?: Product['storeExclusive']
  className?: string
  onRemove?: () => void
  /** If available, the size label to show on QuickAdd button. */
  preselectedSizeLabel?: string
}

export const WishlistLineItem = ({
  title,
  handle,
  images,
  isGiftCard = false,
  customProductType,
  frontImageIndex,
  productType,
  seoCaption,
  rawPrice,
  rawCompareAtPrice,
  material,
  color,
  sizes,
  storeExclusive,
  className,
  onRemove,
  preselectedSizeLabel,
}: WishlistLineItemProps): JSX.Element => {
  const { t } = useTranslation('global')
  const { convert } = useExchangeRates()
  const { data: cart } = useCart()
  const isStoreExclusive = isStoreExclusiveProduct({ storeExclusive })

  // Use the same image logic as the cart (getFeaturedImage with proper fallback)
  const featuredImage = getFeaturedImage(
    images,
    isGiftCard,
    handle,
    customProductType,
    frontImageIndex,
  )

  const imageData = {
    loading: false,
    error: false,
    data: featuredImage,
  }

  // Build alt text using template from settings
  const altTemplate = t('wishlistProductAltTemplate')
  const imgAlt = altTemplate
    .replace('{{title}}', title || '')
    .replace('{{color}}', color || '')
    .replace('{{material}}', material || '')

  const isInCart = useMemo(() => {
    if (!cart || !sizes || isEmpty(sizes)) {
      return false
    }

    const sizeIds = new Set(sizes.map((s) => String(s.id)))

    return (cart.items ?? []).some((item) =>
      sizeIds.has(String(item.variant_id)),
    )
  }, [cart, sizes])

  return (
    <Container
      className={className}
      role="listitem"
      aria-label={`${title || 'Product'} - ${material || ''} ${color || ''} - Remove from wishlist`
        .trim()
        .replaceAll(/\s+/g, ' ')}
    >
      <Header>
        <ConditionalLink
          condition={Boolean(handle)}
          href={`/products/${handle}`}
        >
          <ImageWrapper>
            {imageData.loading && <LoadingImage />}
            {imageData.error && <EmptyImage />}
            {!imageData.loading && !imageData.error && imageData.data && (
              <ProductImage
                lazyload
                lqip
                showDefault
                alt={imgAlt}
                draggable={false}
                transitionDuration={50}
                sources={buildIntrinsicWidthSources({
                  src: imageData.data.src,
                })}
                imgSrc={setQueryParams(imageData.data.src, [
                  { name: 'width', value: '30' },
                ])}
              />
            )}
          </ImageWrapper>
        </ConditionalLink>

        <Details>
          <div>
            {productType && !seoCaption && (
              <SansText className="product-type" as="span" fontWeight="500">
                {productType}
              </SansText>
            )}
            <ConditionalLink
              CustomLink={ProductTitleLink}
              condition={Boolean(handle)}
              href={`/products/${handle}`}
            >
              <HorizontalCardTitle>{title}</HorizontalCardTitle>
            </ConditionalLink>

            {seoCaption && (
              <SansText className="seo-caption" as="span">
                {seoCaption}
              </SansText>
            )}

            <ProductDetails hasSeoCaption={Boolean(seoCaption)}>
              {/* When the seo_caption is shown, the material line is redundant (matches the PLP card behavior) */}
              {material && !seoCaption && (
                <SansText className={className} as="span" fontWeight="500">
                  {material}
                </SansText>
              )}
              {color && (
                <SansText className={className} as="span">
                  {color}
                </SansText>
              )}
            </ProductDetails>
          </div>
          <DetailsFooter>
            <Price role="group" aria-label="Product pricing">
              {isNonNullish(rawCompareAtPrice) &&
                isNonNullish(rawPrice) &&
                rawCompareAtPrice > rawPrice && (
                  <HorizontalCardPrice
                    strikethrough
                    color="gray"
                    aria-label={`Original price: ${convert(rawCompareAtPrice)}`}
                    dangerouslySetInnerHTML={{
                      __html: convert(rawCompareAtPrice),
                    }}
                  />
                )}
              {isNonNullish(rawPrice) && (
                <SansText
                  fontWeight="500"
                  size="lg"
                  as="span"
                  aria-label={`Current price: ${convert(rawPrice)}`}
                  dangerouslySetInnerHTML={{
                    __html: convert(rawPrice),
                  }}
                />
              )}
            </Price>
            <RemoveButton
              aria-label={`Remove ${title || 'product'} from wishlist`}
              title={`Remove ${title || 'product'} from wishlist`}
              onClick={() => onRemove?.()}
            >
              {t('wishlistRemoveButton')}
            </RemoveButton>
          </DetailsFooter>
        </Details>
      </Header>

      {handle && sizes && !isEmpty(sizes) && !isStoreExclusive && (
        <QuickAddDropdown
          condensedLayout
          sizes={sizes}
          handle={handle}
          stickToAddedToCart={isInCart}
          // Show size when we know a preselected label (from Swym cprops)
          {...(preselectedSizeLabel
            ? { buttonLabelOverride: preselectedSizeLabel }
            : {})}
        />
      )}
    </Container>
  )
}
